import { useState, useEffect } from 'react';
import { supabase, placeTradeBid } from '../../lib/supabase';
import { toast } from 'react-hot-toast';
import { useUserStore } from '../../lib/userStore';

interface NewBidFormProps {
  auctionId: number;
  leagueId: number;
  onBidPlaced: () => void;
  originalReceiverAssets?: {
    players?: number[];
    picks?: number[];
    goalies?: number[];
  };
}

interface PlayerData {
  player_id: number;
  firstname: string;
  lastname: string;
  position: string;
  team: string;
  is_goalie: boolean;
}

interface PickData {
  id: number;
  round: number;
  year?: number | null;
  overall_pick?: number | null;
}

export function NewBidForm({ auctionId, leagueId, onBidPlaced, originalReceiverAssets }: NewBidFormProps) {
  const { user_id } = useUserStore();
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [targetSide, setTargetSide] = useState<'initiator' | 'target' | ''>('');
  
  // Assets que je propose
  const [selectedMyPlayers, setSelectedMyPlayers] = useState<PlayerData[]>([]);
  const [selectedMyPicks, setSelectedMyPicks] = useState<PickData[]>([]);
  const [selectedMyGoalies, setSelectedMyGoalies] = useState<PlayerData[]>([]);
  
  // Assets que je veux (saisie manuelle pour simplifier)
  const [wantedPlayerIds, setWantedPlayerIds] = useState('');
  const [wantedPickIds, setWantedPickIds] = useState('');
  const [wantedGoalieIds, setWantedGoalieIds] = useState('');

  // Données disponibles
  const [availablePlayers, setAvailablePlayers] = useState<PlayerData[]>([]);
  const [availablePicks, setAvailablePicks] = useState<PickData[]>([]);
  const [loadingAssets, setLoadingAssets] = useState(false);

  useEffect(() => {
    if (showForm) {
      loadAvailableAssets();
    }
  }, [showForm, leagueId]);

  const loadAvailableAssets = async () => {
    setLoadingAssets(true);
    try {
      // Charger mes joueurs (incluant gardiens)
      const { data: playersData, error: playersError } = await supabase.rpc('getmyteamplayer', {
        p_league_id: leagueId
      });

      if (playersError && playersError.code !== 'PGRST116') {
        throw playersError;
      }

      setAvailablePlayers(playersData || []);

      // Charger mes choix de draft en deux étapes (éviter draftpicks!inner qui nécessite FK exposée)
      const { data: picksLinkRows, error: picksLinkError } = await supabase
        .from('draftpicks_user_league')
        .select(`id, draftpicks_id`)
        .eq('league_id', leagueId)
        .eq('user_id', user_id);

      if (picksLinkError && picksLinkError.code !== 'PGRST116') {
        throw picksLinkError;
      }

      const linkRows = picksLinkRows || [];
      if (linkRows.length === 0) {
        setAvailablePicks([]);
      } else {
        // Récupérer les détails depuis draft_picks en utilisant pick_index
        const pickIndexes = linkRows.map((r: any) => r.draftpicks_id).filter((v: any) => v != null);
        const { data: pickDetails, error: pickDetailsError } = await supabase
          .from('draft_picks')
          .select('pick_index, round_number, league_id, position_in_round, picked_at')
          .in('pick_index', pickIndexes)
          .eq('league_id', leagueId);

        if (pickDetailsError && pickDetailsError.code !== 'PGRST116') {
          throw pickDetailsError;
        }

        const details = pickDetails || [];
        // Map to PickData shape expected by the UI (id, round, year, overall_pick)
        const mapped = linkRows.map((r: any) => {
          const detail = details.find((d: any) => d.pick_index === r.draftpicks_id && d.league_id === leagueId);
          return {
            id: r.id,
            round: detail ? detail.round_number : null,
            year: detail && detail.picked_at ? new Date(detail.picked_at).getFullYear() : null,
            overall_pick: detail ? detail.position_in_round : null
          };
        });

        setAvailablePicks(mapped);
      }
    } catch (error) {
      console.error('Erreur chargement assets:', error);
      toast.error('Erreur lors du chargement de vos assets');
    } finally {
      setLoadingAssets(false);
    }
  };

  

  const handleSubmitBid = async () => {
    if (loading) return;

    // Validation basique
    const totalOffered = selectedMyPlayers.length + selectedMyPicks.length + selectedMyGoalies.length;
    if (totalOffered === 0) {
      toast.error('Vous devez proposer au moins un élément');
      return;
    }

    const wantedPlayerIdsList = wantedPlayerIds.split(',').map(id => id.trim()).filter(id => id && !isNaN(Number(id))).map(Number);
    const wantedPickIdsList = wantedPickIds.split(',').map(id => id.trim()).filter(id => id && !isNaN(Number(id))).map(Number);
    const wantedGoalieIdsList = wantedGoalieIds.split(',').map(id => id.trim()).filter(id => id && !isNaN(Number(id))).map(Number);

    const totalWanted = wantedPlayerIdsList.length + wantedPickIdsList.length + wantedGoalieIdsList.length;
    if (totalWanted === 0) {
      toast.error('Vous devez demander au moins un élément en retour');
      return;
    }

    // Validate that at least one wanted element is part of the original exchange
    if (originalReceiverAssets) {
      const origPlayers = originalReceiverAssets.players || [];
      const origPicks = originalReceiverAssets.picks || [];
      const origGoalies = originalReceiverAssets.goalies || [];

      const playersIntersect = wantedPlayerIdsList.some(id => origPlayers.includes(id));
      const picksIntersect = wantedPickIdsList.some(id => origPicks.includes(id));
      const goaliesIntersect = wantedGoalieIdsList.some(id => origGoalies.includes(id));

      if (!(playersIntersect || picksIntersect || goaliesIntersect)) {
        toast.error('Vous devez demander au moins un élément provenant de l\'échange original');
        return;
      }
    }

    setLoading(true);
    try {
      const bidSenderAssets = {
        players: selectedMyPlayers.map(p => p.player_id),
        picks: selectedMyPicks.map(p => p.id),
        goalies: selectedMyGoalies.map(g => g.player_id)
      };

      const bidReceiverAssets = {
        players: wantedPlayerIdsList,
        picks: wantedPickIdsList,
        goalies: wantedGoalieIdsList
      };

      const { data, error } = await placeTradeBid(
        auctionId,
        Number(user_id),
        bidSenderAssets,
        bidReceiverAssets,
        targetSide || undefined
      );

      if (error) throw error;

      if (data && data.success) {
        toast.success(data.message);
        setShowForm(false);
        resetForm();
        onBidPlaced();
      } else {
        toast.error((data && data.error) || 'Erreur lors de la soumission de l\'offre');
      }
    } catch (error) {
      console.error('Erreur soumission offre:', error);
      toast.error('Erreur lors de la soumission de l\'offre');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedMyPlayers([]);
    setSelectedMyPicks([]);
    setSelectedMyGoalies([]);
    setWantedPlayerIds('');
    setWantedPickIds('');
    setWantedGoalieIds('');
  };

  const togglePlayerSelection = (player: PlayerData) => {
    if (player.is_goalie) {
      setSelectedMyGoalies(prev => 
        prev.find(p => p.player_id === player.player_id)
          ? prev.filter(p => p.player_id !== player.player_id)
          : [...prev, player]
      );
    } else {
      setSelectedMyPlayers(prev => 
        prev.find(p => p.player_id === player.player_id)
          ? prev.filter(p => p.player_id !== player.player_id)
          : [...prev, player]
      );
    }
  };

  const togglePickSelection = (pick: PickData) => {
    setSelectedMyPicks(prev => 
      prev.find(p => p.id === pick.id)
        ? prev.filter(p => p.id !== pick.id)
        : [...prev, pick]
    );
  };

  const parseIds = (s: string) => s.split(',').map(id => id.trim()).filter(id => id && !isNaN(Number(id))).map(Number);

  const wantedPlayerIdsListLive = parseIds(wantedPlayerIds);
  const wantedPickIdsListLive = parseIds(wantedPickIds);
  const wantedGoalieIdsListLive = parseIds(wantedGoalieIds);

  const isWantedIntersectingOriginal = () => {
    if (!originalReceiverAssets) return true;
    const origPlayers = originalReceiverAssets.players || [];
    const origPicks = originalReceiverAssets.picks || [];
    const origGoalies = originalReceiverAssets.goalies || [];

    return (
      wantedPlayerIdsListLive.some(id => origPlayers.includes(id)) ||
      wantedPickIdsListLive.some(id => origPicks.includes(id)) ||
      wantedGoalieIdsListLive.some(id => origGoalies.includes(id))
    );
  };

  if (!showForm) {
    return (
      <div className="mt-6 p-4 border-2 border-dashed border-primary/30 rounded-lg text-center">
        <button 
          onClick={() => setShowForm(true)}
          className="btn btn-primary"
        >
          💰 Faire une contre-offre
        </button>
      </div>
    );
  }

  return (
    <div className="mt-6 p-4 bg-base-200 rounded-lg">
      <div className="flex justify-between items-center mb-4">
        <h4 className="font-semibold">💰 Nouvelle Contre-Offre</h4>
        <button 
          onClick={() => setShowForm(false)}
          className="btn btn-circle btn-sm"
        >
          ✕
        </button>
      </div>

      {loadingAssets ? (
        <div className="flex justify-center py-8">
          <span className="loading loading-spinner loading-lg"></span>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Cible de la contre-offre */}
          <div className="lg:col-span-2">
            <div className="mb-4 p-3 border border-base-300 rounded">
              <h6 className="text-sm font-medium mb-2">🎯 À qui envoyez-vous la contre-offre ?</h6>
              <div className="flex flex-col gap-2">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    className="radio radio-sm"
                    name="targetSide"
                    value="initiator"
                    checked={targetSide === 'initiator'}
                    onChange={() => setTargetSide('initiator')}
                  />
                  <span className="text-sm">Au DG initiateur</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    className="radio radio-sm"
                    name="targetSide"
                    value="target"
                    checked={targetSide === 'target'}
                    onChange={() => setTargetSide('target')}
                  />
                  <span className="text-sm">Au DG cible</span>
                </label>
              </div>
            </div>
          </div>
          {/* Ce que je propose */}
          <div>
            <h5 className="font-medium mb-3">🎯 Ce que je propose :</h5>
            
            {/* Mes joueurs */}
            <div className="mb-4">
              <h6 className="text-sm font-medium mb-2">Joueurs ({selectedMyPlayers.length} sélectionnés)</h6>
              <div className="max-h-32 overflow-y-auto border border-base-300 rounded p-2">
                {availablePlayers.filter(p => !p.is_goalie).map(player => (
                  <label key={player.player_id} className="flex items-center gap-2 p-1 hover:bg-base-300 rounded cursor-pointer">
                    <input
                      type="checkbox"
                      className="checkbox checkbox-sm"
                      checked={selectedMyPlayers.some(p => p.player_id === player.player_id)}
                      onChange={() => togglePlayerSelection(player)}
                    />
                    <span className="text-sm">
                      {player.firstname} {player.lastname} ({player.position}) - {player.team}
                    </span>
                  </label>
                ))}
                {availablePlayers.filter(p => !p.is_goalie).length === 0 && (
                  <p className="text-sm opacity-60 p-2">Aucun joueur disponible</p>
                )}
              </div>
            </div>

            {/* Mes gardiens */}
            <div className="mb-4">
              <h6 className="text-sm font-medium mb-2">Gardiens ({selectedMyGoalies.length} sélectionnés)</h6>
              <div className="max-h-24 overflow-y-auto border border-base-300 rounded p-2">
                {availablePlayers.filter(p => p.is_goalie).map(goalie => (
                  <label key={goalie.player_id} className="flex items-center gap-2 p-1 hover:bg-base-300 rounded cursor-pointer">
                    <input
                      type="checkbox"
                      className="checkbox checkbox-sm"
                      checked={selectedMyGoalies.some(g => g.player_id === goalie.player_id)}
                      onChange={() => togglePlayerSelection(goalie)}
                    />
                    <span className="text-sm">
                      {goalie.firstname} {goalie.lastname} - {goalie.team}
                    </span>
                  </label>
                ))}
                {availablePlayers.filter(p => p.is_goalie).length === 0 && (
                  <p className="text-sm opacity-60 p-2">Aucun gardien disponible</p>
                )}
              </div>
            </div>

            {/* Mes choix de draft */}
            <div className="mb-4">
              <h6 className="text-sm font-medium mb-2">Choix de Draft ({selectedMyPicks.length} sélectionnés)</h6>
              <div className="max-h-32 overflow-y-auto border border-base-300 rounded p-2">
                {availablePicks.map(pick => (
                  <label key={pick.id} className="flex items-center gap-2 p-1 hover:bg-base-300 rounded cursor-pointer">
                    <input
                      type="checkbox"
                      className="checkbox checkbox-sm"
                      checked={selectedMyPicks.some(p => p.id === pick.id)}
                      onChange={() => togglePickSelection(pick)}
                    />
                    <span className="text-sm">
                      {pick.round}e ronde {pick.year}
                      {pick.overall_pick && ` (#${pick.overall_pick})`}
                    </span>
                  </label>
                ))}
                {availablePicks.length === 0 && (
                  <p className="text-sm opacity-60 p-2">Aucun choix disponible</p>
                )}
              </div>
            </div>
          </div>

          {/* Ce que je veux */}
          <div>
            <h5 className="font-medium mb-3">💎 Ce que je veux :</h5>
            <p className="text-sm opacity-70 mb-3">
              Spécifiez les IDs des éléments que vous souhaitez recevoir (séparés par des virgules)
            </p>
            
            <div className="space-y-3">
              <div>
                <label className="label">
                  <span className="label-text">Joueurs demandés (IDs)</span>
                </label>
                <input
                  type="text"
                  className="input input-bordered w-full"
                  placeholder="ex: 123, 456, 789"
                  value={wantedPlayerIds}
                  onChange={(e) => setWantedPlayerIds(e.target.value)}
                />
                <div className="label">
                  <span className="label-text-alt opacity-60">
                    Entrez les IDs des joueurs séparés par des virgules
                  </span>
                </div>
              </div>

              <div>
                <label className="label">
                  <span className="label-text">Gardiens demandés (IDs)</span>
                </label>
                <input
                  type="text"
                  className="input input-bordered w-full"
                  placeholder="ex: 234, 567"
                  value={wantedGoalieIds}
                  onChange={(e) => setWantedGoalieIds(e.target.value)}
                />
              </div>

              <div>
                <label className="label">
                  <span className="label-text">Choix de draft demandés (IDs)</span>
                </label>
                <input
                  type="text"
                  className="input input-bordered w-full"
                  placeholder="ex: 123, 456"
                  value={wantedPickIds}
                  onChange={(e) => setWantedPickIds(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-3 justify-end mt-6">
        <button 
          onClick={() => setShowForm(false)}
          className="btn btn-ghost"
          disabled={loading}
        >
          Annuler
        </button>
        <div className="flex flex-col items-end">
          {!isWantedIntersectingOriginal() && (
            <div className="mb-2 w-full" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div className="w-full p-3 rounded" style={{ background: 'linear-gradient(90deg, rgba(185,28,28,0.12), rgba(185,28,28,0.06))', border: '1px solid rgba(185,28,28,0.6)', color: '#7f1d1d', fontWeight: 700 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="#7f1d1d" viewBox="0 0 24 24">
                    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm1 17h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                  </svg>
                  <span style={{ fontSize: '0.95rem' }}>Vous devez demander au moins un élément provenant de l'échange initial dans la section "Vous voulez" pour soumettre une contre-offre.</span>
                </div>
              </div>
            </div>
          )}
          <button 
            onClick={handleSubmitBid}
            className="btn btn-primary"
            disabled={loading || loadingAssets || !isWantedIntersectingOriginal()}
          >
            {loading ? (
              <span className="loading loading-spinner loading-sm"></span>
            ) : (
              'Soumettre l\'offre'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}